import { FC, useEffect, useState } from "react";
import moment from "moment-timezone";
import { ReactComponent as UberEasts } from "../../../assets/icons/uber-logo.svg";
import { ReactComponent as GrubNub } from "../../../assets/icons/grubhub.svg";
import { ReactComponent as Resy } from "../../../assets/icons/resy.svg";
import { ReactComponent as Yelp } from "../../../assets/icons/yelp.svg";
import { ReactComponent as Doordash } from "../../../assets/icons/doordash.svg";
import { ReactComponent as Sauce } from "../../../assets/icons/sauce.svg";
import { ReactComponent as OpenTable } from "../../../assets/icons/openTable.svg";
import {
  setActiveCategory,
  startLoading,
  stopLoading,
} from "../../../store/redux/menuSlice";
import { localizeCategoryName } from "../../../utils/localization/apply";
import { uiString, uiTemplate } from "../../../utils/localization/uiStrings";
import { useMenuLocaleInit } from "../../../utils/hook/useMenuLocaleInit";
import { useMenuLanguage } from "../../../utils/hook/useMenuLanguage";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../utils/hook/redux-hooks";
import { useIsDesktop } from "../../../utils/hook/useIsDesktop";

export type Item = {
  title: string;
  label: string;
  active: boolean;
  icon?: FC<any>;
};

export type WorkingHours = {
  id?: number;
  mondayStart?: string | null;
  mondayEnd?: string | null;
  tuesdayStart?: string | null;
  tuesdayEnd?: string | null;
  wednesdayStart?: string | null;
  wednesdayEnd?: string | null;
  thursdayStart?: string | null;
  thursdayEnd?: string | null;
  fridayStart?: string | null;
  fridayEnd?: string | null;
  saturdayStart?: string | null;
  saturdayEnd?: string | null;
  sundayStart?: string | null;
  sundayEnd?: string | null;
};

export type DayKey =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

export type WeekDay = {
  key: DayKey;
  name: string;
  start: string;
  end: string;
  isWorkingDay: boolean;
};

export type PartnerKey =
  | "uberEasts"
  | "grubNub"
  | "resy"
  | "yelp"
  | "sauce"
  | "doordash"
  | "openTable";

export type Partner = {
  key: PartnerKey;
  Logo: FC<any>;
  link: string | null;
};

const DAY_KEYS: DayKey[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const PARTNER_SOURCES: {
  key: PartnerKey;
  enabledField: string;
  linkField: string;
  Logo: FC<any>;
}[] = [
  { key: "uberEasts", enabledField: "UberEasts", linkField: "UberEastsLink", Logo: UberEasts },
  { key: "grubNub", enabledField: "GrubNub", linkField: "GrubNubLink", Logo: GrubNub },
  { key: "resy", enabledField: "Resy", linkField: "ResyLink", Logo: Resy },
  { key: "yelp", enabledField: "Yelp", linkField: "YelpLink", Logo: Yelp },
  { key: "sauce", enabledField: "Sauce", linkField: "SauceLink", Logo: Sauce },
  { key: "doordash", enabledField: "Doordash", linkField: "DoordashLink", Logo: Doordash },
  { key: "openTable", enabledField: "openTable", linkField: "openTableLink", Logo: OpenTable },
];

export function useHeaderMenu() {
  const { menu, activeCategory, locale } = useAppSelector((state) => state.menu);
  const { locales, showLanguageSwitcher, changeLocale } = useMenuLanguage();
  const workingHours = menu?.workingHours ? menu.workingHours : null;
  const partners = menu?.partners ? menu.partners : null;
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();
  useMenuLocaleInit();

  const [items, setItems] = useState<Item[]>([]);
  const [isOpen, setIsOpen] = useState(true);
  const [status, setStatus] = useState("");
  const [openCloseTime, setOpenCloseTime] = useState("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [collapsedTime, setCollapsedTime] = useState(false);

  const convertTo12HourFormat = (time: string | null, str: string = "") => {
    if (!time) {
      return "";
    }
    if (time === "00:00:00") {
      return "12:00 AM";
    }
    return moment(time, "HH:mm:ss").format("h:mm A");
  };

  const getNextOpenDay = (dayIndex: number) => {
    const days = DAY_KEYS;

    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (dayIndex + i) % 7;
      const nextDay = days[nextDayIndex];
      const nextDayStart = workingHours[`${nextDay}Start`];
      const nextDayEnd = workingHours[`${nextDay}End`];

      if (nextDayStart && nextDayEnd) {
        return { day: nextDay, start: nextDayStart };
      }
    }

    return null;
  };

  const checkOpenStatus = () => {
    if (!workingHours) return;

    const now = moment.tz("America/New_York");
    const day = now.format("dddd").toLowerCase();

    const start = workingHours[`${day}Start`];
    const end = workingHours[`${day}End`];

    if (!start || !end) {
      setStatus(uiString(locale, "closed"));
      setOpenCloseTime(uiString(locale, "noWorkingHours"));
      setLocationOpen(false);
      return;
    }

    const today = now.clone().startOf("day");
    const startTime = today.clone().add(moment.duration(start));
    let endTime = today.clone().add(moment.duration(end));

    if (end === "00:00:00") {
      endTime = today.clone().add(1, "day");
    } else if (endTime.isBefore(startTime)) {
      endTime = today.clone().add(1, "day").add(moment.duration(end));
    }

    if (now.isBetween(startTime, endTime, null, "[]")) {
      setStatus(uiString(locale, "open"));
      setOpenCloseTime(
        uiTemplate(locale, "closesAt", { time: convertTo12HourFormat(end) })
      );
      setLocationOpen(true);
    } else {
      const nextOpen = getNextOpenDay(now.day());
      if (nextOpen) {
        setStatus(uiString(locale, "closed"));
        setOpenCloseTime(
          uiTemplate(locale, "opensAt", {
            time: convertTo12HourFormat(nextOpen.start),
          })
        );
        setLocationOpen(false);
      } else {
        setStatus(uiString(locale, "closed"));
        setOpenCloseTime(uiString(locale, "noUpcomingHours"));
        setLocationOpen(false);
      }
    }
  };

  useEffect(() => {
    checkOpenStatus();
  }, [workingHours, locale]);

  const formatTime = (
    start: string,
    end: string,
    isWorkingDay: boolean | null
  ) => {
    if (!start || !end) {
      return uiString(locale, "closed");
    }
    if (isWorkingDay === false) {
      return uiString(locale, "closedToday");
    }
    if (start.toLowerCase() === "closed" || end.toLowerCase() === "closed") {
      return uiString(locale, "closed");
    }
    return `${start} – ${end}`;
  };

  const weekDays: WeekDay[] = workingHours
    ? DAY_KEYS.map((key) => ({
        key,
        name: uiString(locale, key),
        start: convertTo12HourFormat(workingHours[`${key}Start`]),
        end: convertTo12HourFormat(workingHours[`${key}End`]),
        isWorkingDay: !!(workingHours[`${key}Start`] && workingHours[`${key}End`]),
      }))
    : [];

  const openDays = weekDays.filter((day) => day.isWorkingDay);
  const weekDaysMondayFirst = weekDays.length
    ? [...weekDays.slice(1), weekDays[0]]
    : [];
  const todayKey = moment.tz("America/New_York").format("dddd").toLowerCase();

  const activePartners: Partner[] = partners
    ? PARTNER_SOURCES.filter((source) => partners[source.enabledField]).map(
        ({ key, Logo, linkField }) => ({
          key,
          Logo,
          link: partners[linkField] ?? null,
        })
      )
    : [];

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (menu?.categories) {
      const categories = menu.categories.map((category, index) => ({
        title: category.name,
        label: localizeCategoryName(category, locale),
        active: activeCategory ? category.name === activeCategory : index === 0,
      }));
      setItems(categories);
      if (!activeCategory && categories[0]) {
        dispatch(setActiveCategory(categories[0].title));
      }
    }
  }, [menu, locale]);

  useEffect(() => {
    if (menu) {
      setItems((prevItems) =>
        prevItems.map((item) => {
          if (item.title === activeCategory) {
            return { ...item, active: true };
          }
          return { ...item, active: false };
        })
      );
    }
  }, [activeCategory]);

  const handleItemPress = async (title: string) => {
    const scrollPosition = window.scrollY;

    dispatch(startLoading());
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.title === title && !item.active) {
          return { ...item, active: true };
        }
        return { ...item, active: false };
      })
    );

    dispatch(setActiveCategory(title));
    dispatch(stopLoading());

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 50);
  };

  const handleLinkClick = (url: string | null) => {
    if (!url) return;
    window.location.href = url;
  };

  const handlePhoneClick = (phone: string | null) => {
    if (!phone) return;
    window.location.href = `tel:${phone}`;
  };

  const handleMapClick = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      window.location.href = `https://maps.apple.com/?q=${encodedAddress}`;
    } else {
      window.location.href = `geo:0,0?q=${encodedAddress}`;
    }
  };

  const hasFullHeader = !!(
    menu?.locationVideo &&
    menu?.address &&
    menu?.phoneNumber &&
    menu?.workingHours.length !== 0
  );

  // @ts-ignore locationVideo is a media array in Strapi, typed here as a single Video
  const videoUrl: string | null = menu?.locationVideo?.[0]?.url ?? null;

  return {
    menu,
    locale,
    isDesktop,
    locales,
    showLanguageSwitcher,
    changeLocale,
    hasFullHeader,
    videoUrl,
    status,
    openCloseTime,
    locationOpen,
    collapsedTime,
    setCollapsedTime,
    isOpen,
    toggleOpen,
    weekDays,
    openDays,
    weekDaysMondayFirst,
    todayKey,
    formatTime,
    partners,
    activePartners,
    items,
    handleItemPress,
    handleLinkClick,
    handlePhoneClick,
    handleMapClick,
  };
}
